import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

// Sous-ensemble de l'API JavaScript de Cloudflare Turnstile utilise par le front.
export interface TurnstileApi {
  render(element: HTMLElement, options: TurnstileOptions): string;
  remove(widgetId: string): void;
}

export interface TurnstileOptions {
  sitekey: string;
  language: string;
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
}

export const TURNSTILE_SCRIPT_URL =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Widget anti-robot de l'inscription (TD-019) : son jeton part dans X-Turnstile-Token.
@Injectable({ providedIn: 'root' })
export class TurnstileService {
  private readonly document = inject(DOCUMENT);
  private api: Promise<TurnstileApi> | null = null;

  // Affiche le widget dans `element` ; onToken recoit le jeton, ou null quand il expire ou echoue.
  // Renvoie la fonction qui retire le widget.
  async render(element: HTMLElement, onToken: (token: string | null) => void): Promise<() => void> {
    const siteKey = environment.turnstileSiteKey;
    if (!siteKey) {
      throw new Error("Turnstile n'est pas configuré pour cet environnement.");
    }
    const turnstile = await this.load();
    const widgetId = turnstile.render(element, {
      sitekey: siteKey,
      language: 'fr',
      callback: (token) => onToken(token),
      'expired-callback': () => onToken(null),
      'error-callback': () => onToken(null),
    });
    return () => turnstile.remove(widgetId);
  }

  private load(): Promise<TurnstileApi> {
    const loaded = this.window().turnstile;
    if (loaded) {
      return Promise.resolve(loaded);
    }
    this.api ??= new Promise<TurnstileApi>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.addEventListener('load', () => {
        const turnstile = this.window().turnstile;
        if (turnstile) {
          resolve(turnstile);
        } else {
          reject(new Error('Turnstile est indisponible.'));
        }
      });
      script.addEventListener('error', () => {
        this.api = null;
        reject(new Error('Turnstile est indisponible.'));
      });
      this.document.head.appendChild(script);
    });
    return this.api;
  }

  private window(): { turnstile?: TurnstileApi } {
    return this.document.defaultView as unknown as { turnstile?: TurnstileApi };
  }
}
